import { useState, useEffect, useRef, useCallback } from 'react';

interface UseFloatingControlsOptions {
  /**
   * Intersection threshold values (default: [0, 0.1])
   * Controls how much of the placeholder needs to be visible
   */
  threshold?: number[];

  /**
   * Root margin for the observer (default: '0px')
   * Allows triggering before element reaches viewport edge
   */
  rootMargin?: string;

  /**
   * Disable floating behavior (useful for testing/debugging)
   */
  disabled?: boolean;
}

interface UseFloatingControlsReturn {
  /**
   * Whether controls should be in floating (fixed) position
   */
  isFloating: boolean;

  /**
   * Ref for the placeholder element (maintains document flow)
   */
  placeholderRef: React.RefObject<HTMLDivElement | null>;

  /**
   * Ref for the controls content (optional, for animations)
   */
  controlsRef: React.RefObject<HTMLDivElement | null>;

  /**
   * Whether floating behavior should be active (mobile check)
   */
  shouldRender: boolean;

  /**
   * Whether user has manually docked the input
   */
  userDocked: boolean;

  /**
   * Whether controls would float based on scroll position (ignoring user override)
   */
  wouldFloat: boolean;

  /**
   * Toggle between docked and floating state
   */
  toggleDocked: () => void;
}

/**
 * Custom hook for managing floating controls behavior on mobile
 *
 * Uses IntersectionObserver to detect when controls reach their natural position.
 * Controls float at bottom of screen until user scrolls to where they naturally sit.
 *
 * @param options - Configuration options for intersection observer
 * @returns State and refs for managing floating controls
 *
 * @example
 * ```tsx
 * const { isFloating, placeholderRef, controlsRef } = useFloatingControls();
 *
 * return (
 *   <>
 *     <div ref={placeholderRef}>
 *       {!isFloating && <div ref={controlsRef}>Controls</div>}
 *     </div>
 *     {isFloating && (
 *       <div className="fixed bottom-0">
 *         <div ref={controlsRef}>Controls</div>
 *       </div>
 *     )}
 *   </>
 * );
 * ```
 */
const SESSION_STORAGE_KEY = 'wallstreetle-floating-docked';

export const useFloatingControls = (
  options: UseFloatingControlsOptions = {}
): UseFloatingControlsReturn => {
  const { threshold = [0, 0.1], rootMargin = '0px', disabled = false } = options;

  // Track whether controls would float based on scroll position
  const [wouldFloat, setWouldFloat] = useState(false);
  // Track whether user has manually docked the input
  const [userDocked, setUserDocked] = useState(() => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true';
  });
  const placeholderRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Toggle docked state and persist to sessionStorage
  const toggleDocked = useCallback(() => {
    setUserDocked((prev) => {
      const newValue = !prev;
      if (typeof window !== 'undefined') {
        if (newValue) {
          sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
        } else {
          sessionStorage.removeItem(SESSION_STORAGE_KEY);
        }
      }
      return newValue;
    });
  }, []);

  useEffect(() => {
    // Skip if disabled
    if (disabled) {
      setWouldFloat(false);
      return;
    }

    const placeholder = placeholderRef.current;
    if (!placeholder) return;

    // Create IntersectionObserver to watch placeholder element
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        // Float controls when:
        // 1. Placeholder is NOT intersecting viewport (not visible)
        // 2. Placeholder is below viewport (positive top value)
        //
        // This means controls float when placeholder is scrolled out of view downward,
        // and return to natural position when user scrolls back up to placeholder
        const shouldFloat = !entry.isIntersecting && entry.boundingClientRect.top > 0;

        setWouldFloat(shouldFloat);
      },
      {
        root: null, // Use viewport as root
        threshold,
        rootMargin
      }
    );

    observer.observe(placeholder);
    observerRef.current = observer;

    // Cleanup observer on unmount
    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [threshold, rootMargin, disabled]);

  // isFloating is false when user has manually docked, regardless of scroll position
  const isFloating = wouldFloat && !disabled && !userDocked;

  return {
    isFloating,
    placeholderRef,
    controlsRef,
    shouldRender: !disabled,
    userDocked,
    wouldFloat: wouldFloat && !disabled,
    toggleDocked
  };
};

export default useFloatingControls;
